import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const DEFAULT_SLOT_OFFER_LIMIT = 3;

async function clinicRequest(path: string, init?: RequestInit) {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) throw new Error("Missing auth token");

  const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) throw new Error("Booking settings request failed");
  return response.json();
}

export default function ClinicBookingSettings() {
  const [slotOfferLimit, setSlotOfferLimit] = useState(DEFAULT_SLOT_OFFER_LIMIT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await clinicRequest("/clinic/slot-offer-limit");
      setSlotOfferLimit(settings.slot_offer_limit ?? DEFAULT_SLOT_OFFER_LIMIT);
    } catch (loadError) {
      console.error("[CLINIC_BOOKING_SETTINGS][LOAD]", loadError);
      setError("Failed to load booking availability settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    if (!Number.isInteger(slotOfferLimit) || slotOfferLimit < 1 || slotOfferLimit > 10) {
      setError("Choose a whole number from 1 to 10.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const settings = await clinicRequest("/clinic/slot-offer-limit", {
        method: "PATCH",
        body: JSON.stringify({ slot_offer_limit: slotOfferLimit }),
      });
      setSlotOfferLimit(settings.slot_offer_limit);
      setSuccess("Booking availability settings saved.");
    } catch (saveError) {
      console.error("[CLINIC_BOOKING_SETTINGS][SAVE]", saveError);
      setError("Failed to save booking availability settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Booking &amp; availability</h2>
        <p className="mt-1 text-sm text-slate-600">
          Choose how many concrete appointment times the bot can propose in one reply.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading booking availability settings…</p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <label className="block text-sm font-medium text-slate-800" htmlFor="slot-offer-limit">
            Maximum appointment times to offer
          </label>
          <p className="mt-1 text-sm text-slate-500">
            Enter a value from 1 to 10. The default is 3.
          </p>
          <input
            id="slot-offer-limit"
            type="number"
            min={1}
            max={10}
            step={1}
            value={slotOfferLimit}
            onChange={(event) => {
              setSlotOfferLimit(Number(event.target.value));
              setSuccess(null);
            }}
            className="mt-3 block w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
          />
          {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
          {success && <p className="mt-3 text-sm text-emerald-700" role="status">{success}</p>}
          <button
            type="button"
            disabled={saving}
            onClick={saveSettings}
            className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </section>
  );
}
